import { isValidEmail, isValidPhoneNumber } from 'utils';
import {
	DUPLICATE_REASONS,
	IDENTIFIER_TYPES,
	NEW_NON_EMPLOYEE_KEYS,
} from './_const';
import Yup from 'utils/validation/yup-extensions';
import IdentifierNumberField from './components/fields/IdentifierNumber';
import ThirdPartyOrganizationField from './components/fields/ThirdPartyOrganization';
import ThirdPartyAcknowledgement from './components/fields/ThirdPartyAcknowledgement';
import AlternateEmail from './components/fields/AlternateEmail';
import DateOfBirth from './components/fields/DateOfBirth';
import FirstName from './components/fields/FirstName';
import IdentifierType from './components/fields/IdentifierType';
import LastName from './components/fields/LastName';
import MiddleInitial from './components/fields/MiddleInitial';
import PhoneNumber from './components/fields/PhoneNumber';
import PhoneNumberExtension from './components/fields/PhoneNumberExtension';
import PrerequisiteCheck from './components/views/PrerequisiteCheck';
import DuplicateIdentity from './components/fields/DuplicateIdentity';
import { getOldestValidDOB, getYoungestValidDOB } from './_util';
import { checkForExistingEmail } from './_api';
import SiteAdminCheck from './components/fields/SiteAdminCheck';
import SponsorSelect from './components/fields/SponsorSelect';
import SelfSponsor from './components/fields/SelfSponsor';
import SiteAdminRoleCheck from './components/views/SiteAdminRoleCheck';
import CommunityConnectLocation from './components/fields/CommunityConnectLocation';

/**
 * Overarching non-employee form configuration
 * contains default values to populate the field on form load
 * contains Yup-enabled validationSchema object
 * contains reference to default component developed for the workflow
 */
export const NEW_NON_EMPLOYEE_CONFIG = {
	[NEW_NON_EMPLOYEE_KEYS.ACKNOWLEDGEMENT]: {
		initialValue: false,
		validationSchema: Yup.bool().oneOf(
			[true],
			'You must acknowledge to proceed.'
		),
		reviewLabel: 'Third-party organization acknowledgement',
		component: <ThirdPartyAcknowledgement />,
	},
	[NEW_NON_EMPLOYEE_KEYS.ALTERNATE_EMAIL]: {
		initialValue: '',
		validationSchema: Yup.string()
			.required(
				'Please provide a personal alternate email address for the new user.'
			)
			.maxCounter(255)
			.test('isValidEmail', async function (value) {
				if (value) {
					return (
						isValidEmail(value) ||
						this.createError({
							message: 'Invalid Email Address Format',
						})
					);
				} else {
					return true;
				}
			})
			.test('isNonUpmcEmail', async function (value) {
				if (
					value &&
					(value.toLowerCase().includes('@upmc.tst') ||
						value.toLowerCase().includes('@upmc.edu'))
				) {
					return this.createError({
						message:
							'You may not provide a UPMC email address for this value. This should be a personal email address.',
					});
				}
				return true;
			})
			.test('isInUse', async function (value) {
				if (value) {
					const claimed = await checkForExistingEmail(value);
					if (claimed) {
						return this.createError({
							message:
								'This email address has been claimed by an existing E-Sign-On account.',
						});
					} else {
						return true;
					}
				}
				return true;
			}),
		reviewLabel: 'Personal email',
		component: <AlternateEmail />,
	},
	[NEW_NON_EMPLOYEE_KEYS.DATE_OF_BIRTH]: {
		initialValue: '',
		validationSchema: Yup.date()
			.required('A valid date of birth is required.')
			.min(
				getOldestValidDOB(),
				'Invalid birth date. Please provide a realistic date in the past.'
			)
			.max(
				getYoungestValidDOB(),
				'Invalid birth date. Please provide a date indicating legal working age.'
			),
		reviewLabel: 'Date of Birth',
		component: <DateOfBirth />,
	},
	[NEW_NON_EMPLOYEE_KEYS.FIRST_NAME]: {
		initialValue: '',
		validationSchema: Yup.string()
			.trim()
			.required('The first name is required.')
			.matches(
				/^[a-zA-Z`'\\. -]*$/,
				'The value provided contains invalid characters'
			)
			.maxCounter(32),
		reviewLabel: 'First Name',
		component: <FirstName />,
	},
	[NEW_NON_EMPLOYEE_KEYS.IDENTIFIER_NUMBER]: {
		initialValue: '',
		validationSchema: Yup.mixed()
			.when([NEW_NON_EMPLOYEE_KEYS.IDENTIFIER_TYPE], {
				is: IDENTIFIER_TYPES.SOCIAL_SECURITY_NUMBER,
				then: Yup.string()
					.minCounter(11)
					.maxCounter(11)
					.matches(
						/^[0-9]{3}-[0-9]{2}-[0-9]{4}$/,
						'SSN should be numbers separated by hyphens in the format of XXX-XX-XXXX.'
					)
					.required('SSN is required.'),
			})
			.when([NEW_NON_EMPLOYEE_KEYS.IDENTIFIER_TYPE], {
				is: IDENTIFIER_TYPES.VISA,
				then: Yup.string()
					.trim()
					.required('Visa number is required.')
					.isAlphaNumeric()
					.minCounter(4)
					.maxCounter(75),
			})
			.when([NEW_NON_EMPLOYEE_KEYS.IDENTIFIER_TYPE], {
				is: IDENTIFIER_TYPES.PASSPORT,
				then: Yup.string()
					.trim()
					.required('Passport number is required.')
					.isAlphaNumeric()
					.minCounter(4)
					.maxCounter(75),
			})
			.when([NEW_NON_EMPLOYEE_KEYS.IDENTIFIER_TYPE], {
				is: IDENTIFIER_TYPES.DRIVERS_LICENSE,
				then: Yup.string()
					.trim()
					.required("Driver's License number is required.")
					.isAlphaNumeric()
					.minCounter(4)
					.maxCounter(20),
			})
			.when([NEW_NON_EMPLOYEE_KEYS.IDENTIFIER_TYPE], {
				is: IDENTIFIER_TYPES.EU_IDENTIFIER,
				then: Yup.string()
					.trim()
					.required('EU Identifier number is required.')
					.minCounter(4)
					.maxCounter(20),
			})
			.when([NEW_NON_EMPLOYEE_KEYS.IDENTIFIER_TYPE], {
				is: IDENTIFIER_TYPES.MICROSOFT_ID,
				then: Yup.string()
					.trim()
					.required('Microsoft ID number is required.')
					.minCounter(4)
					.maxCounter(75),
			})
			.when([NEW_NON_EMPLOYEE_KEYS.IDENTIFIER_TYPE], {
				is: IDENTIFIER_TYPES.UNIQUE_SURVEYOR_ID,
				then: Yup.string()
					.trim()
					.required('Surveyor ID number is required.')
					.minCounter(4)
					.maxCounter(75),
			})
			.when([NEW_NON_EMPLOYEE_KEYS.IDENTIFIER_TYPE], {
				is: IDENTIFIER_TYPES.UNIVERSITY_ID,
				then: Yup.string()
					.trim()
					.required('University ID number is required.')
					.isAlphaNumeric()
					.minCounter(4)
					.maxCounter(20),
			})
			.when([NEW_NON_EMPLOYEE_KEYS.IDENTIFIER_TYPE], {
				is: IDENTIFIER_TYPES.PARTNER_ID,
				then: Yup.string()
					.trim()
					.required('Partner ID number is required.')
					.isAlphaNumeric()
					.minCounter(4)
					.maxCounter(19),
			})
			.when([NEW_NON_EMPLOYEE_KEYS.IDENTIFIER_TYPE], {
				is: IDENTIFIER_TYPES.AADHAR_NUMBER,
				then: Yup.number()
					.typeError('Aadhar number should be numbers only.')
					.min(
						111111111111,
						'Too few numbers provided. Expected 12 numbers.'
					)
					.max(
						999999999999,
						'Too many numbers provided. Expected 12 numbers.'
					)
					.required('Aadhar number is required.'),
			}),
		reviewLabel: 'Identifier Number',
		component: <IdentifierNumberField />,
	},
	[NEW_NON_EMPLOYEE_KEYS.IDENTIFIER_TYPE]: {
		initialValue: 4,
		validationSchema: Yup.number().required(),
		reviewLabel: 'Identifier Type',
		component: <IdentifierType />,
	},
	[NEW_NON_EMPLOYEE_KEYS.IDENTIFIER_SPECIFIER]: {
		initialValue: {
			countryName: 'United States',
			stateAbbreviation: 'PA',
			stateName: 'Pennsylvania',
		},
		validationSchema: Yup.mixed()
			.when([NEW_NON_EMPLOYEE_KEYS.IDENTIFIER_TYPE], {
				is: value =>
					[
						IDENTIFIER_TYPES.UNIVERSITY_ID,
						IDENTIFIER_TYPES.PARTNER_ID,
					].includes(value),
				then: Yup.string().required('Required').typeError('Required.'),
			})
			.when([NEW_NON_EMPLOYEE_KEYS.IDENTIFIER_TYPE], {
				is: value => [IDENTIFIER_TYPES.DRIVERS_LICENSE].includes(value),
				then: Yup.object()
					.shape({
						stateAbbreviation: Yup.string().required(),
						stateName: Yup.string().required(),
						countryName: Yup.string().required(),
					})
					.default(undefined)
					.nullable()
					.required('Required')
					.typeError('Required.'),
			}),
		reviewLabel: 'Identifier Specifier',
	},
	[NEW_NON_EMPLOYEE_KEYS.IS_SITE_ADMIN]: {
		initialValue: null,
		validationSchema: Yup.string()
			.oneOf(
				['true', 'false'],
				'Please indicate if the user account you are attempting to create is for a site administrator.'
			)
			.required(
				'Please indicate if the user account you are attempting to create is for a site administrator.'
			),
		reviewLabel: 'Site Administrator?',
		component: <SiteAdminCheck />,
	},
	[NEW_NON_EMPLOYEE_KEYS.JOB_TITLE]: {
		initialValue: null,
		validationSchema: Yup.object()
			.shape({
				jobTitleId: Yup.number().required(),
				jobTitleName: Yup.string().required(),
			})
			.default(undefined)
			.nullable()
			.required(
				'Please select the most appropriate job title for your new user.'
			),
		reviewLabel: 'Third-Party Organization',
		component: <ThirdPartyOrganizationField />,
	},
	[NEW_NON_EMPLOYEE_KEYS.LAST_NAME]: {
		initialValue: '',
		validationSchema: Yup.string()
			.trim()
			.required('The last name is required.')
			.matches(
				/^[a-zA-Z`'\\. -]*$/,
				'The value provided contains invalid characters'
			)
			.maxCounter(32),
		reviewLabel: 'Last Name',
		component: <LastName />,
	},
	[NEW_NON_EMPLOYEE_KEYS.MIDDLE_INITIAL]: {
		initialValue: '',
		validationSchema: Yup.string()
			.trim()
			.matches(
				/^[a-zA-Z]*$/,
				'The value provided contains invalid characters'
			)
			.maxCounter(1),
		reviewLabel: 'Middle Initial',
		component: <MiddleInitial />,
	},
	[NEW_NON_EMPLOYEE_KEYS.NOT_A_DUPLICATE_ACKNOWLEDGEMENT]: {
		initialValue: false,
		validationSchema: Yup.bool().when(
			[NEW_NON_EMPLOYEE_KEYS.DUPLICATE_ID_CHECK],
			{
				is: DUPLICATE_REASONS.SUSPECTED_DUPLICATE_ESO,
				then: Yup.bool().oneOf(
					[true],
					'You must acknowledge to proceed.'
				),
			}
		),
		reviewLabel: 'Acknowledgement of non-duplicate identity',
		// component: <ThirdPartyAcknowledgement />,
	},
	[NEW_NON_EMPLOYEE_KEYS.NOT_A_DUPLICATE_REASON]: {
		initialValue: '',
		validationSchema: Yup.string().when(
			[NEW_NON_EMPLOYEE_KEYS.DUPLICATE_ID_CHECK],
			{
				is: DUPLICATE_REASONS.SUSPECTED_DUPLICATE_ESO,
				then: Yup.string().required(
					'Please provide a reason this is not a duplicate account.'
				),
			}
		),
		reviewLabel: 'Reason',
		// component: <ThirdPartyAcknowledgement />,
	},
	[NEW_NON_EMPLOYEE_KEYS.PHONE_NUMBER]: {
		initialValue: '',
		validationSchema: Yup.string()
			.maxCounter(255)
			.test('isValidPhoneNumber', async function (value) {
				if (value) {
					return (
						isValidPhoneNumber(value, false) ||
						this.createError({
							message: 'Invalid Phone Number Format',
						})
					);
				} else {
					return true;
				}
			}),
		reviewLabel: 'Personal Phone Number',
		component: <PhoneNumber />,
	},
	[NEW_NON_EMPLOYEE_KEYS.PHONE_NUMBER_EXTENSION]: {
		initialValue: '',
		validationSchema: Yup.number()
			.typeError('Please provide numbers only')
			.max(9999999999, 'Too many numbers provided'),
		reviewLabel: 'Phone Number Extension',
		component: <PhoneNumberExtension />,
	},
	[NEW_NON_EMPLOYEE_KEYS.PREREQUISITE_CHECK]: {
		initialValue: null,
		validationSchema: Yup.bool().oneOf(
			[true],
			'Your account is not configured properly to make this kind of request.'
		),
		reviewLabel: 'Prerequisite check',
		component: <PrerequisiteCheck />,
	},
	[NEW_NON_EMPLOYEE_KEYS.DUPLICATE_ID_CHECK]: {
		initialValue: DUPLICATE_REASONS.LOADING,
		validationSchema: Yup.number().oneOf(
			[DUPLICATE_REASONS.NONE, DUPLICATE_REASONS.SUSPECTED_DUPLICATE_ESO],
			'The identity you are attempting to create may already exist in our system.'
		),
		reviewLabel: 'Duplicate Identity Check',
		component: <DuplicateIdentity />,
	},
	[NEW_NON_EMPLOYEE_KEYS.REQUESTOR_IS_SITE_ADMIN]: {
		initialValue: false,
		validationSchema: Yup.bool(),
		reviewLabel: 'Requestor is A Site Administrator check',
		component: <SiteAdminRoleCheck />,
	},
	[NEW_NON_EMPLOYEE_KEYS.SELF_SPONSOR]: {
		initialValue: null,
		validationSchema: Yup.string().when(
			[NEW_NON_EMPLOYEE_KEYS.REQUESTOR_IS_SITE_ADMIN],
			{
				is: value => value === false,
				then: Yup.string()
					.oneOf(
						['true', 'false'],
						'Please indicate you intend to be the sponsor of the newly created account.'
					)
					.required(
						'Please indicate you intend to be the sponsor of the newly created account.'
					),
			}
		),
		reviewLabel: 'Self-sponsor',
		component: <SelfSponsor />,
	},
	[NEW_NON_EMPLOYEE_KEYS.SPONSOR]: {
		initialValue: null,
		validationSchema: Yup.object()
			.when([NEW_NON_EMPLOYEE_KEYS.SELF_SPONSOR], {
				is: value => value === 'false',
				then: Yup.object()
					.shape({
						Name: Yup.object().shape({
							Value: Yup.string().required(),
						}),
					})
					.test('isEmployee', async function (value) {
						if (!value?.Name?.Value) {
							return this.createError({
								message: 'Please select a valid sponsor.',
							});
						} else if (value?.Employee_Status?.Value !== 'true') {
							return this.createError({
								message:
									'The sponsor you have selected needs to be an employee.',
							});
						} else {
							return true;
						}
					})
					.default(undefined)
					.nullable()
					.required('Please select a sponsor.'),
			})
			.default(undefined)
			.nullable(),
		reviewLabel: 'Sponsor',
		component: <SponsorSelect />,
	},
	[NEW_NON_EMPLOYEE_KEYS.THIRD_PARTY_ORGANIZATION]: {
		initialValue: null,
		validationSchema: Yup.object()
			.shape({
				organizationId: Yup.number().required(),
				organizationName: Yup.string().required(),
			})
			.default(undefined)
			.nullable()
			.required(
				'Please select the third-party organization associated with the user.'
			),
		reviewLabel: 'Third-Party Organization',
		component: <ThirdPartyOrganizationField />,
	},
	[NEW_NON_EMPLOYEE_KEYS.WAVE_ONE_LOCATION]: {
		initialValue: null,
		validationSchema: Yup.string()
			.oneOf(
				['true', 'false'],
				'Please indicate if the user account you are attempting to create is going to be working at the listed facilities.'
			)
			.required(
				'Please indicate if the user account you are attempting to create is going to be working at the listed facilities.'
			),
		reviewLabel: 'Wave 1 Location',
		component: <CommunityConnectLocation />,
	},
};
